import React, { useState, useContext, useEffect, useRef } from 'react';
import '../../styles-wa/create_template.css';
import { createWhatsAppTemplate, getApiErrorMessage } from '../../api-wa';
import { ToastContext } from '../../Context/WaToastContext';

const LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'Hindi' },
    { code: 'ta', name: 'Tamil' },
    { code: 'te', name: 'Telugu' },
    { code: 'kn', name: 'Kannada' },
    { code: 'ml', name: 'Malayalam' },
];

const CreateTemplate = ({ onAddTemplate, onCancel }) => {
    // Template Details
    const [templateName, setTemplateName] = useState('');
    const [category, setCategory] = useState('MARKETING');
    const [language, setLanguage] = useState('en');

    // Message Content - Header
    const [headerType, setHeaderType] = useState('NONE');
    const [headerText, setHeaderText] = useState('');
    const [headerImage, setHeaderImage] = useState(null);
    const [headerImagePreview, setHeaderImagePreview] = useState(null);

    // Message Content - Body & Footer
    const [bodyText, setBodyText] = useState('');
    const [footerText, setFooterText] = useState('Reply STOP to opt out.');
    
    // Variables & Samples
    const [variables, setVariables] = useState([]);
    const [sampleValues, setSampleValues] = useState({});

    // Buttons
    const [ctaButtonText, setCtaButtonText] = useState('Visit Website');
    const [ctaUrl, setCtaUrl] = useState('https://merida.com');
    const [hasCta, setHasCta] = useState(true);
    
    const [quickReplies, setQuickReplies] = useState([]);
    
    // UI State
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const { showError, showSuccess } = useContext(ToastContext);
    
    const fileInputRef = useRef(null);

    // Extract variables whenever body text changes
    useEffect(() => {
        const matches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
        const foundVars = matches.map(m => m.replace(/\{\{|\}\}/g, ''));
        
        // Remove duplicates and sort
        const uniqueVars = [...new Set(foundVars)].sort((a, b) => parseInt(a) - parseInt(b));
        setVariables(uniqueVars);
        
        // Cleanup sample values for removed variables
        setSampleValues(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(key => {
                if (!uniqueVars.includes(key)) delete next[key];
            });
            return next;
        });
    }, [bodyText]);

    const handleHeaderImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setHeaderImage(file);
            const reader = new FileReader();
            reader.onloadend = () => setHeaderImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const addVariable = () => {
        const nextNum = variables.length > 0 ? Math.max(...variables.map(v => parseInt(v))) + 1 : 1;
        setBodyText(prev => prev + ` {{${nextNum}}}`);
    };

    const addQuickReply = () => {
        if (quickReplies.length < 3) {
            setQuickReplies([...quickReplies, '']);
        }
    };

    const updateQuickReply = (index, value) => {
        const updated = [...quickReplies];
        updated[index] = value;
        setQuickReplies(updated);
    };

    const removeQuickReply = (index) => {
        setQuickReplies(quickReplies.filter((_, i) => i !== index));
    };

    const validateForm = () => {
        const newErrors = {};
        
        const cleanName = templateName.trim().toLowerCase().replace(/[\s\-]+/g, '_').replace(/[^a-z0-9_]/g, '');
        if (!cleanName) {
            newErrors.templateName = 'Template name is required';
        }

        // Body Text
        if (!bodyText.trim()) {
            newErrors.bodyText = 'Body text is required';
        } else if (bodyText.length > 1024) {
            newErrors.bodyText = 'Body text exceeds 1024 characters';
        } else {
            // Check for single curly braces (invalid variable format)
            if (/(?<!\{)\{(?!\{)/.test(bodyText) || /(?<!\})\}(?!\})/.test(bodyText)) {
                newErrors.bodyText = 'Use double curly braces {{1}} format for variables.';
            } else {
                // Find all double curly brace tokens
                const matches = bodyText.match(/\{\{([^{}]+)\}\}/g) || [];
                const rawTokens = matches.map(m => m.replace(/\{\{|\}\}/g, ''));
                
                // 1. Check if all variables are digits
                const nonDigitTokens = rawTokens.filter(t => !/^\d+$/.test(t));
                if (nonDigitTokens.length > 0) {
                    newErrors.bodyText = 'Invalid variable format. Only numbers are allowed (e.g. {{1}}, {{2}}). Named variables like {{name}} are not supported.';
                } else if (rawTokens.length > 0) {
                    // 2. Check for sequential and starting at 1
                    const sortedNums = [...new Set(rawTokens.map(Number))].sort((a, b) => a - b);
                    const expectedNums = Array.from({ length: sortedNums.length }, (_, i) => i + 1);
                    
                    const isSequential = sortedNums.every((val, index) => val === expectedNums[index]);
                    if (!isSequential) {
                        newErrors.bodyText = 'Variables must be sequential starting at 1 (e.g. {{1}}, {{2}}). Do not skip numbers.';
                    }
                }
            }
        }

        // CTA
        if (hasCta) {
            if (!ctaButtonText.trim()) newErrors.ctaButton = 'Button text is required';
            if (!ctaUrl.trim()) newErrors.ctaUrl = 'URL is required';
            else if (!/^https?:\/\/.+/.test(ctaUrl)) newErrors.ctaUrl = 'Enter a valid URL starting with http:// or https://';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            showError('Please fix the errors before submitting');
            return;
        }

        setLoading(true);
        try {
            const cleanName = templateName.trim().toLowerCase().replace(/[\s\-]+/g, '_').replace(/[^a-z0-9_]/g, '');
            const buttons = [];
            if (hasCta && ctaButtonText.trim() && ctaUrl.trim()) {
                buttons.push({ type: 'URL', text: ctaButtonText.trim(), url: ctaUrl.trim() });
            }
            quickReplies.forEach(qr => {
                if (qr && qr.trim()) {
                    buttons.push({ type: 'QUICK_REPLY', text: qr.trim() });
                }
            });

            const finalSampleValues = { ...sampleValues };
            if ((headerType === 'IMAGE' || headerType === 'VIDEO') && headerImagePreview) {
                finalSampleValues['header_handle'] = headerImagePreview;
            } else if (headerType === 'DOCUMENT' && headerImage) {
                finalSampleValues['header_handle'] = headerImage.name;
            }

            const templatePayload = {
                template_name: cleanName,
                category: category,
                language: language,
                header_type: headerType,
                header_text: headerType === 'TEXT' ? headerText : null,
                body_text: bodyText,
                sample_values: finalSampleValues,
                footer_text: footerText || null,
                buttons: buttons,
                status: 'PENDING_REVIEW'
            };

            const response = await createWhatsAppTemplate(templatePayload);
            showSuccess('Template submitted successfully for review!');
            
            if (onAddTemplate) {
                onAddTemplate(response);
            }
        } catch (err) {
            const errorMsg = getApiErrorMessage(err, 'Failed to submit template');
            showError(errorMsg);
            setLoading(false);
        }
    };

    const getPreviewBody = () => {
        if (!bodyText) return '';
        let text = bodyText;
        // Escape HTML to prevent XSS (sanitize content properly)
        text = text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
            
        // Process variables
        variables.forEach(v => {
            const val = sampleValues[v] || `{{${v}}}`;
            // Since val could contain HTML characters, let's escape it as well
            const escapedVal = String(val)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
            text = text.replace(new RegExp(`\\{\\{${v}\\}\\}` , 'g'), `<span class="preview-var">${escapedVal}</span>`);
        });

        // Convert formatting markers like *bold*, _italic_, ~strikethrough~ to HTML tags safely
        text = text
            .replace(/\*([^\s*\n](?:[^*]*?[^\s*\n])?)\*/g, '<strong>$1</strong>')
            .replace(/_([^\s_\n](?:[^_]*?[^\s_\n])?)_/g, '<em>$1</em>')
            .replace(/~([^\s~\n](?:[^~]*?[^\s~\n])?)~/g, '<s>$1</s>');

        // Convert \n to <br/>
        text = text.replace(/\n/g, '<br/>');

        return text;
    };

    return (
        <main className="main-content create-template-page">
            <div className="template-builder-container">
                {/* LEFT COLUMN: Form Editor */}
                <div className="template-form-section">
                    
                    {/* SECTION 1: Details */}
                    <div className="form-card">
                        <h3 className="section-title">Template Details</h3>
                        <div className="form-row">
                            <div className="form-group flex-2">
                                <label className="form-label">Template Name</label>
                                <input
                                    type="text"
                                    className={`form-input ${errors.templateName ? 'error' : ''}`}
                                    placeholder="e.g., welcome_message_v1"
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value.toLowerCase().replace(/ /g, '_'))}
                                />
                                {errors.templateName ? <span className="error-text">{errors.templateName}</span> : <span className="hint-text">Lowercase, numbers, and underscores only.</span>}
                            </div>

                            <div className="form-group">
                                <label className="form-label">Category</label>
                                <select 
                                    className="form-select" 
                                    value={category} 
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setCategory(val);
                                        if (val === 'AUTHENTICATION') {
                                            setHeaderType('NONE');
                                            setHeaderText('');
                                            setHeaderImage(null);
                                            setHeaderImagePreview(null);
                                            setHasCta(false);
                                            setQuickReplies([]);
                                        }
                                    }}
                                >
                                    <option value="MARKETING">Marketing</option>
                                    <option value="UTILITY">Utility</option>
                                    <option value="AUTHENTICATION">Authentication</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Language</label>
                                <select className="form-select" value={language} onChange={(e) => setLanguage(e.target.value)}>
                                    {LANGUAGES.map(lang => <option key={lang.code} value={lang.code}>{lang.name}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* SECTION 2: Message Content */}
                    <div className="form-card">
                        <h3 className="section-title">Message Content</h3>
                        
                        {/* Header Sub-section */}
                        <div className="form-group" style={{ marginBottom: '24px' }}>
                            <label className="form-label">HEADER (OPTIONAL)</label>
                            {category === 'AUTHENTICATION' ? (
                                <div className="info-box-auth">
                                    <span className="info-text">⚡ ï¸ Headers are not supported for Authentication templates by Meta.</span>
                                </div>
                            ) : (
                                <>
                                    <div className="header-type-selector">
                                        {['NONE', 'TEXT', 'IMAGE', 'DOCUMENT', 'VIDEO'].map(type => (
                                            <button 
                                                key={type} 
                                                className={`type-btn ${headerType === type ? 'active' : ''}`}
                                                onClick={() => setHeaderType(type)}
                                            >
                                                {type}
                                            </button>
                                        ))}
                                    </div>
                                    
                                    {headerType === 'TEXT' && (
                                        <div className="header-content-box">
                                            <div className="input-with-counter">
                                                <input
                                                    type="text"
                                                    className="form-input"
                                                    maxLength={60}
                                                    placeholder="Enter header text..."
                                                    value={headerText}
                                                    onChange={(e) => setHeaderText(e.target.value)}
                                                />
                                                <span className={`char-counter ${headerText.length >= 60 ? 'limit' : ''}`}>{headerText.length}/60</span>
                                            </div>
                                        </div>
                                    )}

                                    {(headerType === 'IMAGE' || headerType === 'DOCUMENT' || headerType === 'VIDEO') && (
                                        <div className="header-content-box">
                                            <div className="file-upload-zone" onClick={() => fileInputRef.current.click()}>
                                                <input 
                                                    type="file" 
                                                    ref={fileInputRef} 
                                                    hidden 
                                                    onChange={handleHeaderImageChange}
                                                    accept={headerType === 'IMAGE' ? "image/*" : headerType === 'VIDEO' ? "video/*" : ".pdf,.doc,.docx"}
                                                />
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                                                <span>{headerImage ? headerImage.name : `Click to upload ${headerType.toLowerCase()}`}</span>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Body Sub-section */}
                        <div className="form-group" style={{ marginBottom: '24px' }}>
                            <label className="form-label">BODY TEXT</label>
                            <div className="textarea-container">
                                <textarea
                                    className={`form-textarea ${errors.bodyText ? 'error' : ''}`}
                                    value={bodyText}
                                    maxLength={1024}
                                    onChange={(e) => setBodyText(e.target.value)}
                                    placeholder="Enter your message here..."
                                ></textarea>
                                <span className={`char-counter body ${bodyText.length >= 1024 ? 'limit' : ''}`}>{bodyText.length}/1024</span>
                            </div>
                            {errors.bodyText && <span className="error-text">{errors.bodyText}</span>}
                            
                            <button className="btn-add-variable" onClick={addVariable}>+ Add Variable {'{{x}}'}</button>
                            
                            {/* Variable Samples */}
                            {variables.length > 0 && (
                                <div className="variable-samples-container">
                                    <p className="sample-title">Add sample values for variables:</p>
                                    <div className="samples-grid">
                                        {variables.map(v => (
                                            <div key={v} className="sample-item">
                                                <span className="sample-label">{'{{'}{v}{'}}'}</span>
                                                <input
                                                    type="text"
                                                    className="sample-input"
                                                    placeholder="Enter sample..."
                                                    value={sampleValues[v] || ''}
                                                    onChange={(e) => setSampleValues({ ...sampleValues, [v]: e.target.value })}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer Sub-section */}
                        <div className="form-group">
                            <label className="form-label">FOOTER (OPTIONAL)</label>
                            <div className="input-with-counter">
                                <input
                                    type="text"
                                    className="form-input"
                                    maxLength={60}
                                    value={footerText}
                                    onChange={(e) => setFooterText(e.target.value)}
                                    placeholder="Short text at the bottom..."
                                />
                                <span className={`char-counter ${footerText.length >= 60 ? 'limit' : ''}`}>{footerText.length}/60</span>
                            </div>
                        </div>
                    </div>

                    {/* SECTION 3: Buttons */}
                    <div className="form-card">
                        <h3 className="section-title">Interactive Buttons</h3>
                        {category === 'AUTHENTICATION' ? (
                            <div className="info-box-auth">
                                <span className="info-text">⚡ ï¸ Custom Call-to-Action and Quick Reply buttons are not supported for Authentication templates by Meta. If you need headers or custom buttons, please change the category to Utility or Marketing.</span>
                            </div>
                        ) : (
                            <>
                                {/* CTA Section */}
                                <div className="button-editor-box">
                                    <div className="editor-header">
                                        <label className="form-label">CALL TO ACTION BUTTON</label>
                                        <div className="toggle-switch">
                                            <input type="checkbox" id="cta-toggle" checked={hasCta} onChange={() => setHasCta(!hasCta)} />
                                            <label htmlFor="cta-toggle"></label>
                                        </div>
                                    </div>
                                    
                                    {hasCta && (
                                        <div className="cta-fields">
                                            <div className="form-row">
                                                <div className="form-group">
                                                    <label className="sub-label">Button Text</label>
                                                    <input 
                                                        type="text" 
                                                        className={`form-input sm ${errors.ctaButton ? 'error' : ''}`}
                                                        value={ctaButtonText} 
                                                        onChange={(e) => setCtaButtonText(e.target.value)} 
                                                    />
                                                </div>
                                                <div className="form-group flex-2">
                                                    <label className="sub-label">Website URL</label>
                                                    <input 
                                                        type="text" 
                                                        className={`form-input sm ${errors.ctaUrl ? 'error' : ''}`}
                                                        value={ctaUrl} 
                                                        onChange={(e) => setCtaUrl(e.target.value)} 
                                                    />
                                                </div>
                                            </div>
                                            {(errors.ctaButton || errors.ctaUrl) && <span className="error-text">{errors.ctaButton || errors.ctaUrl}</span>}
                                        </div>
                                    )}
                                </div>

                                {/* Quick Replies Section */}
                                <div className="button-editor-box" style={{ marginTop: '20px' }}>
                                    <div className="editor-header">
                                        <label className="form-label">QUICK REPLY BUTTONS (UP TO 3)</label>
                                        <button 
                                            className="btn-add-qr" 
                                            disabled={quickReplies.length >= 3}
                                            onClick={addQuickReply}
                                        >
                                            + Add Quick Reply
                                        </button>
                                    </div>
                                    
                                    <div className="qr-list">
                                        {quickReplies.map((qr, index) => (
                                            <div key={index} className="qr-item">
                                                <div className="input-container">
                                                    <input
                                                        type="text"
                                                        className={`form-input sm ${errors[`qr_${index}`] ? 'error' : ''}`}
                                                        placeholder="e.g., Yes"
                                                        maxLength={25}
                                                        value={qr}
                                                        onChange={(e) => updateQuickReply(index, e.target.value)}
                                                    />
                                                    <span className="qr-counter">{qr.length}/25</span>
                                                </div>
                                                <button className="btn-remove-qr" onClick={() => removeQuickReply(index)}>&times;</button>
                                                {errors[`qr_${index}`] && <span className="error-text">{errors[`qr_${index}`]}</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* RIGHT COLUMN: Live Phone Preview */}
                <div className="template-preview-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 className="preview-label" style={{ margin: 0 }}>Live Preview</h3>
                        <div className="creator-actions">
                            <button onClick={onCancel} disabled={loading} className="btn-secondary">Cancel</button>
                            <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
                                {loading ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </div>

                    <div className="phone-mockup">
                        <div className="phone-header">
                            <div className="avatar" style={{ width: '32px', height: '32px', fontSize: '12px' }}>M</div>
                            <div className="wa-business-info">
                                <span className="wa-name">Merida Tech Mind</span>
                                <span className="wa-status">online</span>
                            </div>
                        </div>

                        <div className="phone-body">
                            <div className="wa-bubble">
                                {/* Header Preview */}
                                {headerType === 'TEXT' && headerText && <div className="wa-header-text">{headerText}</div>}
                                {headerType === 'IMAGE' && headerImagePreview && (
                                    <div className="wa-header-media">
                                        <img src={headerImagePreview} alt="Header" />
                                    </div>
                                )}
                                {headerType === 'VIDEO' && headerImagePreview && (
                                    <div className="wa-header-media">
                                        <video src={headerImagePreview} controls style={{ width: '100%', borderRadius: '8px', maxHeight: '160px' }} />
                                    </div>
                                )}
                                {headerType === 'VIDEO' && !headerImagePreview && (
                                    <div className="wa-header-media doc" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg>
                                        <span>Click to upload video</span>
                                    </div>
                                )}
                                {headerType === 'DOCUMENT' && (
                                    <div className="wa-header-media doc">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline></svg>
                                        <span>{headerImage ? headerImage.name : 'document.pdf'}</span>
                                    </div>
                                )}

                                {/* Body Preview */}
                                <div 
                                    className="wa-body-text message-body" 
                                    style={{ paddingBottom: '4px' }}
                                    dangerouslySetInnerHTML={{ __html: getPreviewBody() || '<span class="placeholder">Message body will appear here...</span>' }}
                                />

                                {/* Footer Preview */}
                                {footerText && <div className="wa-footer" style={{ marginBottom: '4px' }}>{footerText}</div>}
                                
                                {/* Buttons Preview (WhatsApp Native Attached Style) */}
                                {(hasCta || quickReplies.some(qr => qr)) && (
                                    <div className="wa-bubble-buttons">
                                        {hasCta && ctaButtonText && (
                                            <div className="wa-bubble-btn">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                                                {ctaButtonText}
                                            </div>
                                        )}
                                        {quickReplies.map((qr, idx) => (
                                            qr && (
                                                <div key={idx} className="wa-bubble-btn">
                                                    {qr}
                                                </div>
                                            )
                                        ))}
                                    </div>
                                )}
                                
                                <div className="wa-bubble-tail"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default CreateTemplate;



